import { isSfwSite } from '../../js/config.js';
import { tokenManager } from '../../js/utils/TokenManager.js';
import { normalizeLang, getCurrentLanguage } from '../../js/translations.js';
import { fetchUserCreatedAt } from '../../js/utils/userProfileApi.js';

const isWithin48Hours = (createdAt) => {
  if (!createdAt) return false;
  const hoursDiff = (Date.now() - new Date(createdAt).getTime()) / (1000 * 60 * 60);
  return hoursDiff <= 48;
};

const getRemainingTime = (createdAt) => {
  const endTime = new Date(createdAt).getTime() + (48 * 60 * 60 * 1000);
  const remaining = endTime - Date.now();
  if (remaining <= 0) return { total: 0, hours: 0, minutes: 0, seconds: 0 };

  return {
    total: remaining,
    hours: Math.floor(remaining / (1000 * 60 * 60)),
    minutes: Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((remaining % (1000 * 60)) / 1000)
  };
};

/**
 * @param {{ userData: object, saveUserData?: Function }} options
 * @returns {Promise<string|null>}
 */
export async function getUserCreatedAt({ userData, saveUserData }) {
  if (userData?.created_at) return userData.created_at;

  try {
    const token = await tokenManager.getValidAccessToken();
    if (!token) return null;
    const createdAt = await fetchUserCreatedAt({ token });
    if (createdAt) saveUserData?.({ created_at: createdAt });
    return createdAt;
  } catch (error) {
    console.error('[TrialOffer] Error fetching user created_at:', error);
  }
  return null;
}

/**
 * @param {string} createdAt
 * @returns {HTMLElement}
 */
export function createCountdownButton(createdAt) {
  const btn = document.createElement('a');
  btn.href = '#';
  btn.className = 'trial-offer-nav-button trial-countdown-button';

  const updateCountdown = () => {
    const remaining = getRemainingTime(createdAt);
    if (remaining.total <= 0) {
      clearInterval(interval);
      btn.parentNode?.removeChild(btn);
    } else {
      const h = String(remaining.hours).padStart(2, '0');
      const m = String(remaining.minutes).padStart(2, '0');
      const s = String(remaining.seconds).padStart(2, '0');
      btn.textContent = `${h}:${m}:${s}`;
    }
  };

  updateCountdown();
  const interval = setInterval(updateCountdown, 1000);
  btn._countdownInterval = interval;

  return btn;
}

/**
 * @param {{ userData: object, saveUserData?: Function }} options
 * @returns {Promise<HTMLElement|null>}
 */
export async function createTrialOfferButton({ userData, saveUserData }) {
  if (!isSfwSite()) return null;
  const currentTier = userData?.current_tier || 'free';
  if (currentTier !== 'free') return null;

  const createdAt = await getUserCreatedAt({ userData, saveUserData });
  if (!createdAt || !isWithin48Hours(createdAt)) return null;

  return createCountdownButton(createdAt);
}

/**
 * @param {HTMLElement} button
 * @param {{ track?: Function }} options
 */
export function addTrialButtonEventListeners(button, { track } = {}) {
  const openTrialOffer = async (e) => {
    e.preventDefault();
    track?.('nav_trial_offer_click');
    try {
      const { showTrialOfferModal } = await import('../../js/utils/quotaErrorHandler.js');
      await showTrialOfferModal();
    } catch (err) {
      console.error('Failed to open trial offer modal:', err);
    }
  };

  button.addEventListener('click', openTrialOffer);
  button.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openTrialOffer(e); }
  });
  button.addEventListener('mouseenter', () => button.classList.add('trial-countdown-button--hover'));
  button.addEventListener('mouseleave', () => button.classList.remove('trial-countdown-button--hover'));
}

/**
 * @param {{ userData: object, language: string, saveUserData?: Function }} options
 */
export async function showTrialOfferToastIfNeeded({ userData, language, saveUserData }) {
  try {
    if (!isSfwSite()) return;
    if (sessionStorage.getItem('trial_offer_warning_shown')) return;
    if (!userData?.isLoggedIn) return;

    const currentTier = userData?.current_tier || 'free';
    if (currentTier !== 'free') return;

    const createdAt = userData?.created_at || await getUserCreatedAt({ userData, saveUserData });
    if (!createdAt || !isWithin48Hours(createdAt)) return;

    const remaining = getRemainingTime(createdAt);
    if (remaining.total <= 0) return;

    const timeStr = [
      String(remaining.hours).padStart(2, '0'),
      String(remaining.minutes).padStart(2, '0'),
      String(remaining.seconds).padStart(2, '0')
    ].join(':');

    const lang = normalizeLang(language || getCurrentLanguage());
    const navTranslations = (window.translations?.[lang]?.nav) || (window.translations?.en?.nav) || {};
    const toastTemplate =
      navTranslations.trial_offer_toast ||
      '⚠️ Trial offer expiring in {time} - Upgrade for unlimited messages!';
    const toastMessage = toastTemplate.replace('{time}', timeStr);

    const { showWarning } = await import('../../js/utils/UnifiedNotificationSystem.js');
    showWarning(toastMessage, {
      duration: 7000,
      position: 'bottom-right',
      onClick: async () => {
        try {
          const { showTrialOfferModal } = await import('../../js/utils/quotaErrorHandler.js');
          await showTrialOfferModal();
        } catch (err) {
          console.error('[TrialOffer] Failed to open trial offer modal:', err);
        }
      }
    });

    sessionStorage.setItem('trial_offer_warning_shown', 'true');
  } catch (error) {
    console.error('[TrialOffer] Failed to show trial offer toast:', error);
  }
}
